import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import React, { useCallback, useRef, useState } from 'react';
import classnames from 'classnames';
import { Skeleton } from './skeleton/skeleton';

interface Props {
    src: string;
    poster?: string;
    /**
     * Eagerly loads and autoplays the video. Use for the initially visible video only;
     * non-priority videos load metadata upfront and start playing (and downloading)
     * once they become visible.
     */
    isPriority?: boolean;
    wrapperClassname?: string;
    className?: string;
    onClick?: () => void;
    location: string;
    telemetry: ITelemetry;
    variantConcatenatedId?: string;
}

const HAVE_CURRENT_DATA = 2;
const VISIBILITY_THRESHOLD = 0.5;

// Handles video autoplay and pause while being hidden. Needed for Safari, works by default on other browsers
const observeVideoVisibility = (video: HTMLVideoElement): IntersectionObserver | undefined => {
    if (typeof IntersectionObserver === 'undefined') {
        return;
    }

    const observer = new IntersectionObserver(
        ([entry]) => {
            if (entry.isIntersecting) {
                video.play().catch(() => {});
            } else {
                video.pause();
            }
        },
        { threshold: VISIBILITY_THRESHOLD }
    );
    observer.observe(video);
    return observer;
};

export const VideoWithLoading = ({
    src,
    poster,
    isPriority = false,
    wrapperClassname,
    className,
    onClick,
    location,
    telemetry,
    variantConcatenatedId
}: Props) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const observerRef = useRef<IntersectionObserver>();

    // Handles the case where the video already has data before hydration,
    // in which case the loadedData event will never fire.
    const videoRef = useCallback((node: HTMLVideoElement | null) => {
        observerRef.current?.disconnect();
        observerRef.current = undefined;

        if (!node) {
            return;
        }
        if (node.readyState >= HAVE_CURRENT_DATA) {
            setIsLoaded(true);
        }
        observerRef.current = observeVideoVisibility(node);
    }, []);

    const handleError = () => {
        telemetry.error(
            `[video-with-loading][${location}] Video failed to load (variantId: ${variantConcatenatedId ?? 'unknown'}) (src: ${src})`
        );
        setIsLoaded(true);
    };

    const handleOnLoadedData = () => {
        setIsLoaded(true);
    };

    return (
        <div className={classnames('video-with-loading', wrapperClassname)} onClick={onClick}>
            {!isLoaded && <Skeleton className='video-with-loading__skeleton' />}
            <video
                ref={videoRef}
                className={classnames('video-with-loading__video', className, {
                    'video-with-loading__video--loaded': isLoaded
                })}
                src={src}
                poster={poster}
                autoPlay={isPriority}
                loop
                muted
                playsInline
                preload={isPriority ? 'auto' : 'metadata'}
                onLoadedData={handleOnLoadedData}
                onError={handleError}
            />
        </div>
    );
};
